import React, { useState } from "react";
import { Button, Card, Col, Descriptions, Drawer, Form, Menu, message, Modal, Row, Select, Table } from "antd";
import { CheckOutlined, CloseOutlined, EyeOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import { useTranslation } from "react-i18next";
import EllipsisDropdown from "../../../../../components/shared-components/EllipsisDropdown";
import styled from "styled-components";
import { arrayUnion, doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../../../configs/FirebaseConfig";
import { isMobile } from "../../../../../functions/browser";
import {
  FIRESTORE_CUSTOMERS_TABLE,
  FIRESTORE_ORDERS_TABLE,
  FIRESTORE_REGIONS_TABLE,
  FIRESTORE_USERS_TABLE,
} from "../../../../../constants/FirestoreConstant";
import { CHEFS_FARM, COMPANY_COUNTRY_CODE } from "../../../../../constants/CompanyConstants";
import { USER_STATUS_ACTIVE, USER_STATUS_REJECTED } from "../../../../../constants/UserConstant";
import { ORDER_STATUS_CREATED } from "../../../../../constants/OrderConstant";
import { useOrders } from "../../../../../data/hooks/firestore/useOrders";
import Loading from "../../../../../components/shared-components/Loading";
import { useCustomers } from "../../../../../data/hooks/firestore/useCustomers";
import { useWebshopUsers } from "../../../../../data/hooks/firestore/useWebshopUsers";
import AddCompanyModal from "../modals/AddCompanyModal";
import axios from "axios";
import urls from "../../../../../urls";
import dayjs from "dayjs";
import { useAuth } from "../../../../../data/hooks/firebase/useAuth";
import { DeliveryZoneBadge } from "../../../../../functions/deliveryZones";
import { useCountries } from "../../../../../data/hooks/firestore/useCountries";

const { Option } = Select;

const PendingCompanies = () => {
  const { t } = useTranslation();
  const { email } = useAuth();
  const { newCustomers, customers, isLoading: customersLoading } = useCustomers();
  const { webshopUsers: users, isLoading: webshopUsersLoading } = useWebshopUsers();
  const { data: orders, loading: ordersLoading } = useOrders();
  const { getCountryDelivery } = useCountries();
  const [linkCompanyForm] = Form.useForm();
  const [drawerUser, setDrawerUser] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(null);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [linkCompanyModalOpen, setLinkCompanyModalOpen] = useState(false);

  const confirmRejectUser = (request) => {
    Modal.confirm({
      title: t("confirm_reject_customer_title"),
      content: t("confirm_reject_customer_message", { company: request.customerName }),
      okText: t("reject"),
      cancelText: t("cancel"),
      okButtonProps: { danger: true },
      onOk: () => rejectUser(request),
    });
  };

  const rejectUser = async (request) => {
    setIsLoading(true);
    try {
      await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, request.id), {
        status: "rejected",
        updatedAt: dayjs().unix(),
        updatedBy: email,
      });

      // Send rejection email
      const user = users.find((u) => u.id === request.customerUsers[0]);
      try {
        await axios.post(urls.sendCustomerRequestRejectedMail, {
          email: request.customerEmail,
          contactPerson: user
            ? `${user.userFirstName} ${user.userLastName}`
            : request.contact?.firstName + " " + request.contact?.lastName,
          companyName: request.customerName,
          region: request.customerRegion,
          country: request.country,
          userId: request.customerUsers?.[0] || null,
        });
      } catch (emailError) {
        console.error("Failed to send rejection email:", emailError);
        // Don't fail the whole operation if email fails
      }

      message.success(t("request_rejected"));
      closeDrawer();
    } catch (error) {
      console.error("Failed to reject user:", error);
      message.error(t("error"));
    } finally {
      setIsLoading(false);
    }
  };

  const dropdownMenu = (row) => (
    <Menu>
      <Menu.Item
        onClick={() => {
          const user = users.find((u) => u.id === row.customerUsers[0]);
          setDrawerUser({ ...row, user: user });
          setDrawerVisible(true);
        }}
      >
        <Flex alignItems="center">
          <EyeOutlined />
          <span style={{ marginLeft: 4 }}>{t("details")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item
        onClick={() => {
          const user = users.find((u) => u.id === row.customerUsers[0]);
          setDrawerUser({ ...row, user: user });
          setLinkCompanyModalOpen(true);
        }}
      >
        <Flex alignItems="center">
          <UsergroupAddOutlined />
          <span style={{ marginLeft: 4 }}>{t("link_customer")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item
        onClick={() => {
          onPressEditCustomer(row);
        }}
      >
        <Flex alignItems="center">
          <CheckOutlined />
          <span style={{ marginLeft: 4 }}>{t("accept")}</span>
        </Flex>
      </Menu.Item>
      <Menu.Item
        onClick={() => {
          confirmRejectUser(row);
        }}
      >
        <Flex alignItems="center">
          <CloseOutlined />
          <span style={{ marginLeft: 4 }}>{t("reject")}</span>
        </Flex>
      </Menu.Item>
    </Menu>
  );

  const tableColumns = [
    {
      title: t("company"),
      dataIndex: "customerName",
      render: (text, record) => {
        const countryConfig = getCountryDelivery(record.country);

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <DeliveryZoneBadge zipCode={record.zipCode} country={record.country} countryConfig={countryConfig} />
            <button
              className={"text-button"}
              onClick={() => {
                const user = users.find((u) => u.id === record.customerUsers[0]);
                setDrawerUser({ ...record, user });
                setDrawerVisible(true);
              }}
            >
              <span style={{ minWidth: "120px", display: "block" }}>{text}</span>
            </button>
          </div>
        );
      },
    },
    {
      title: t("contact_person"),
      dataIndex: "contactPerson",
      render: (_, customer) => {
        const user = users.find((u) => u.id === customer.customerUsers[0]);
        if (!!user) return <span>{user.userFirstName + " " + user.userLastName}</span>;
        else return "-";
      },
    },
    {
      title: t("country"),
      dataIndex: "country",
      render: (data) => <span>{t(data)}</span>,
    },
    {
      title: t("address"),
      dataIndex: "customerAddress",
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, elm) => (
        <div className="text-right">
          <EllipsisDropdown menu={dropdownMenu(elm)} />
        </div>
      ),
    },
  ];

  const onPressEditCustomer = (customer) => {
    const user = users.find((u) => u.id === customer.customerUsers[0]);

    // Build contact info from user or existing contact
    const contact = {
      firstName: user?.userFirstName || customer.contact?.firstName || "",
      lastName: user?.userLastName || customer.contact?.lastName || "",
    };

    setCustomerToEdit({
      ...customer,
      contact,
    });
    setEditModalVisible(true);
  };

  const handleLinkCompany = () => {
    linkCompanyForm.validateFields().then(async (values) => {
      setIsLoading(true);

      try {
        const { customerId } = values;
        const customer = customers.find((c) => c.id === customerId);
        if (!customer) {
          message.error(t("notifications.company_not_found"));
          return;
        }

        // Gelinkte farmorders ophalen en updaten als die er zijn
        const linkedOrders = orders.filter((o) => o.farmorderCustomerId === drawerUser.user.userCustomerId);
        if (linkedOrders.length > 0) {
          for (const order of linkedOrders) {
            await updateDoc(
              doc(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_ORDERS_TABLE, order.id),
              {
                farmorderCustomerId: customer.id,
                farmorderCustomerName: customer.customerName,
                farmorderStatus: ORDER_STATUS_CREATED,
              },
            ).catch((err) => {
              throw new Error("Could not update linked order with id " + order.id);
            });
          }
        }

        // Customer id, name en status aanpassen in user record
        await updateDoc(doc(firestore, FIRESTORE_USERS_TABLE, drawerUser.user.id), {
          userCustomerId: customer.id,
          userCustomerName: customer.customerName,
          userStatus: USER_STATUS_ACTIVE,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        }).catch((err) => {
          throw new Error("Could not update user with id " + drawerUser.user.id);
        });

        // userId toevoegen aan customerUsers list
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, customer.id), {
          customerUsers: arrayUnion(drawerUser.user.id),
          updatedAt: dayjs().unix(),
          updatedBy: email,
        }).catch((err) => {
          throw new Error("Could not update customer with id " + customer.id);
        });

        // Aangemaakt bedrijf verwijderen
        await updateDoc(doc(firestore, FIRESTORE_CUSTOMERS_TABLE, drawerUser.user.userCustomerId), {
          status: USER_STATUS_REJECTED,
          updatedAt: dayjs().unix(),
          updatedBy: email,
        });
        closeLinkCompanyModal();
      } catch (err) {
        console.log(err);
        setIsLoading(false);
        message.error(err.message);
      }
    });
  };

  const closeEditModal = () => {
    setCustomerToEdit(null);
    setEditModalVisible(false);
  };

  const closeLinkCompanyModal = () => {
    setDrawerUser(null);
    linkCompanyForm.resetFields();
    setLinkCompanyModalOpen(false);
    setIsLoading(false);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    setDrawerUser(null);
    setIsLoading(false);
  };

  if (ordersLoading || customersLoading || webshopUsersLoading) {
    return <Loading />;
  }

  return (
    <>
      <Row className={"mb-2"}>
        <Col xs={24} md={16} xl={15} xxl={14}>
          <h1>
            {t("customers")} - {t("customers_pending")}
          </h1>
        </Col>
        <Col xs={24}>
          <Card>
            <div className="table-responsive">
              <Table
                columns={tableColumns}
                dataSource={newCustomers}
                rowKey="id"
                scroll={{ x: 600 }}
                loading={customersLoading}
              />
            </div>
          </Card>

          {/* Use AddCompanyModal in edit mode */}
          <AddCompanyModal
            visible={editModalVisible}
            onClose={closeEditModal}
            initialValues={customerToEdit}
            exactCustomers={[]}
          />

          <Drawer
            title={t("company_data", { company: !!drawerUser && drawerUser.customerName })}
            placement="right"
            onClose={closeDrawer}
            open={drawerVisible}
            width={isMobile ? window.innerWidth : 850}
          >
            {!!drawerUser && (
              <>
                <Descriptions bordered size="small" column={1}>
                  <Descriptions.Item
                    label={
                      drawerUser.company === CHEFS_FARM || !drawerUser.company
                        ? t("restaurant_name")
                        : t("customer_name")
                    }
                  >
                    {drawerUser.customerName}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("contact_person")}>
                    {!!drawerUser.user ? `${drawerUser.user?.userFirstName} ${drawerUser.user?.userLastName}` : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("country")}>{t(drawerUser.country)}</Descriptions.Item>
                  <Descriptions.Item label={t("address")}>{drawerUser.customerAddress}</Descriptions.Item>
                  {drawerUser.customerAddress !== drawerUser.customerBillingAddress &&
                    !!drawerUser.customerBillingAddress && (
                      <Descriptions.Item label={t("billing_address")}>
                        {drawerUser.customerBillingAddress}
                      </Descriptions.Item>
                    )}
                  <Descriptions.Item label={t("payment_method")}>
                    {t(drawerUser.paymentMethod.toLowerCase().replace(" ", "_"))}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("kvk_number")}>{drawerUser.kvkNumber}</Descriptions.Item>
                  <Descriptions.Item label={t("email")}>{drawerUser.customerEmail}</Descriptions.Item>
                  <Descriptions.Item label={t("phone")}>{drawerUser.customerPhone}</Descriptions.Item>
                  <Descriptions.Item label={t("delivery_days")}>
                    {!!drawerUser.customerDeliveryDays ? drawerUser.customerDeliveryDays : "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("delivery_label")}>
                    {!!drawerUser.deliveryLabel ? drawerUser.deliveryLabel : "-"}
                  </Descriptions.Item>
                </Descriptions>
                <ButtonsWrapper>
                  <Button onClick={() => onPressEditCustomer(drawerUser)}>{t("edit_data")}</Button>
                  <div>
                    <Button
                      loading={isLoading}
                      onClick={() => confirmRejectUser(drawerUser)}
                      style={{ marginLeft: 12 }}
                    >
                      {t("reject")}
                    </Button>
                  </div>
                </ButtonsWrapper>
              </>
            )}
          </Drawer>
        </Col>
      </Row>
      <Modal
        title={t("link_customer")}
        open={linkCompanyModalOpen}
        onOk={handleLinkCompany}
        onCancel={closeLinkCompanyModal}
        okText={t("save")}
        cancelText={t("close")}
      >
        <Form layout="vertical" form={linkCompanyForm} onFinish={handleLinkCompany}>
          <Form.Item name={"customerId"} label={t("company")}>
            <Select showSearch optionFilterProp="children" allowClear placeholder={t("form.choose_company")}>
              {customers?.map((customer) => (
                <Option key={customer.customerId} value={customer.customerId}>
                  {customer.customerName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

const ButtonsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 1rem;
`;

export default PendingCompanies;
